import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

//components
import { SearchBox } from "../components/commons/search.box";

//stores
import { searchCustomers, clearCustomers, setCustomerSearch } from "../stores/customers";

const PAGE_SIZE = 10;

export const TableCustomersList = () => {
  // hooks
  const dispatch = useDispatch();
   const {
    lists = [],
    loading = false,
    keyword = "",
    attribute = "email",
  } = useSelector((c) => c.customers);

  const [currentPage, setCurrentPage] = useState(1);

  // Minimum input for email and phone number
  const MIN_EMAIL_CHARS = 3;
  const MIN_PHONE_DIGITS = 5;

  // change 019 to +6019 (Cognito stores E.164)
  const toE164Prefix = (raw) => {
    const digits = raw.replace(/\D/g, ""); // keep digits only
    if (!digits) return "";
    const national = digits.replace(/^0/, ""); // drop a leading 0
    return `+60${national}`;
  };

  // ===== DEBOUNCED SEARCH =====
  useEffect(() => {
    const raw = keyword.trim();

    // Decide the minimum and the value we actually send to Cognito
    let term = raw;
    if (attribute === "phone_number") {
      const digits = raw.replace(/\D/g, "");
      if (digits.length < MIN_PHONE_DIGITS) {
        dispatch(clearCustomers());
        return;
      }
      term = toE164Prefix(raw);
    } else {
      if (raw.length < MIN_EMAIL_CHARS) {
        dispatch(clearCustomers());
        return;
      }
    }

    const timer = setTimeout(async () => {
      try {
        await dispatch(searchCustomers({ attribute, keyword: term })).unwrap();
      } catch (err) {
        console.error("tables.customers.list@search", err);
        toast.error("Failed to search customers");
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [keyword, attribute, dispatch]);

  // reset to first page on every new search / attribute change
  useEffect(() => {
    setCurrentPage(1);
  }, [keyword, attribute]);

  // client-side paging over the Cognito result set
  const totalPages = Math.max(1, Math.ceil(lists.length / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const displayedCustomers = lists.slice(startIndex, startIndex + PAGE_SIZE);

  return (
    <div className="mt-8">
      {/* Search bar — matches List Partner (right-aligned, icon inside, with a type dropdown) */}
      <div className="mb-4">
        <SearchBox
          value={keyword}
          onChange={(val) => dispatch(setCustomerSearch({ keyword: val }))}
          placeholder={
            attribute === "email"
              ? "Search by email (min 3 letters)..."
              : "Search by phone (min 5 digits, e.g. 01947)..."
          }
          options={[
            { value: "email", label: "Email" },
            { value: "phone_number", label: "Phone number" },
          ]}
          selected={attribute}
          onSelect={(val) => {
            dispatch(setCustomerSearch({ attribute: val, keyword: "" }));
            dispatch(clearCustomers());
          }}
        />
      </div>


      <div className="rounded-md bg-white shadow-md">
        <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-100 px-4 py-4">
          <p className="text-sm text-slate-600">
            Showing {displayedCustomers.length} of {lists.length} customer
            {lists.length === 1 ? "" : "s"}
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              className="rounded px-3 py-1 text-sm disabled:text-gray-300"
            >
              &lsaquo;
            </button>

            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-500 text-sm text-white">
              {currentPage}
            </span>

            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() =>
                setCurrentPage((page) => Math.min(totalPages, page + 1))
              }
              className="rounded px-3 py-1 text-sm disabled:text-gray-300"
            >
              &rsaquo;
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
          <thead className="border-y border-slate-200 bg-slate-100">
            <tr>
              <th className="whitespace-nowrap px-4 py-4 text-left text-sm">Name</th>
              <th className="whitespace-nowrap px-4 py-4 text-left text-sm">Email</th>
              <th className="whitespace-nowrap px-4 py-4 text-left text-sm">Phone</th>
              <th className="whitespace-nowrap px-4 py-4 text-left text-sm">Status</th>
              <th className="whitespace-nowrap px-4 py-4 text-left text-sm">Enabled</th>

            </tr>
          </thead>

          <tbody className="divide-y divide-slate-200">
            {displayedCustomers.map((user) => (
              <tr key={user.username}>
                <td className="whitespace-nowrap px-4 py-4 text-sm">
                  <Link
                    to={`/customers/details/${user.sub}`}
                    state={{
                      name: user.name,
                      email: user.email,
                      phone_number: user.phone_number,
                      status: user.status,
                      enabled: user.enabled,
                    }}
                    className="font-bold text-blue-600 underline"
                  >
                    {user.name || "-"}
                  </Link>
                </td>
                <td className="whitespace-nowrap px-4 py-4 text-sm text-slate-600">
                  {user.email}
                </td>
                <td className="whitespace-nowrap px-4 py-4 text-sm text-slate-600">
                  {user.phone_number || "-"}
                </td>
                <td className="px-4 py-4">
                  <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                    {user.status}
                  </span>
                </td>
                <td className="px-4 py-4 text-sm text-slate-600">
                  {user.enabled ? "Yes" : "No"}
                </td>
              </tr>
            ))}
          </tbody>
          </table>
        </div>

        {/* Prompt state — nothing typed yet */}
        {!loading && !keyword.trim() && lists.length === 0 && (
          <div className="p-8 text-center">
            <MagnifyingGlassIcon
              className="mx-auto h-8 w-8 text-gray-300"
              aria-hidden="true"
            />
            <p className="mt-2 font-semibold text-gray-500">Search customers</p>
            <p className="mt-1 text-sm text-gray-400">
              Find by email (min 3 letters) or phone (min 5 digits, e.g. 01947).
            </p>
          </div>
        )}

        {/* No-results state — typed enough but nothing matched */}
        {!loading && keyword.trim() && lists.length === 0 && (
          <div className="p-8 text-center">
            <p className="font-semibold text-gray-500">No customers found</p>
            <p className="mt-1 text-sm text-gray-400">
              No results for "{keyword}".
            </p>
          </div>
        )}

        {loading && (
          <div className="p-8 text-center text-sm text-gray-500">
            Searching customers...
          </div>
        )}
      </div>
    </div>
  );
};
